import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { baseUrl } from 'libs/core/src/lib/configs/baseUrl';
import { Observable } from 'rxjs';
import {
  createQueryParams,
  IInsurer,
  IInsurerQueryParams,
  IInsurerRepresentative,
  IInsurerRequest,
  IInsurersStat,
  IInsurerStats,
  IInsurerUpdate,
  InsurerSettings,
  IRepresentative,
  IRepresentativeRequest,
  PaginatedResponse,
} from '@inshuwa-client/models';
import { NgHttpCachingHeaders } from 'ng-http-caching';

@Injectable({
  providedIn: 'root',
})
export class InsurerService {
  constructor(private http: HttpClient) {}

  getAllInsurers(
    param?: Partial<IInsurerQueryParams>,
  ): Observable<PaginatedResponse<IInsurer>> {
    return this.http.get<PaginatedResponse<IInsurer>>(`${baseUrl()}/insurers`, {
      params: createQueryParams(param),
    });
  }

  getInsurerById(id: any): Observable<IInsurer> {
    return this.http.get<IInsurer>(`${baseUrl()}/insurers/${id}`);
  }

  getStats(
    id: number,
    param?: Partial<IInsurerQueryParams>,
  ): Observable<IInsurerStats> {
    return this.http.get<IInsurerStats>(
      `${baseUrl()}/insurers/${id}/stats/insurer`,
      { params: createQueryParams(param) },
    );
  }

  getStat(param?: Partial<IInsurerQueryParams>): Observable<IInsurersStat> {
    return this.http.get<IInsurersStat>(`${baseUrl()}/insurers/stats`, {
      params: createQueryParams(param),
    });
  }

  updateInsurer(
    id: number,
    payload: Partial<IInsurerUpdate>,
  ): Observable<IInsurer> {
    return this.http.put<IInsurer>(`${baseUrl()}/insurers/${id}`, payload);
  }

  addInsurer(request: Partial<IInsurerRequest>): Observable<IInsurer> {
    return this.http.post<IInsurer>(`${baseUrl()}/insurers/`, request);
  }

  addRepresentative(
    id: number,
    request: Partial<IInsurerRepresentative>,
  ): Observable<IInsurerRepresentative> {
    return this.http.post<IInsurerRepresentative>(
      `${baseUrl()}/insurers/${id}/representatives`,
      request,
    );
  }

  getRepresentatives(
    id: number,
    param?: Partial<IInsurerQueryParams>,
    noCache: boolean = false,
  ): Observable<PaginatedResponse<IInsurerRepresentative>> {
    const HttpCatchingHeaders = noCache
      ? NgHttpCachingHeaders.DISALLOW_CACHE
      : NgHttpCachingHeaders.ALLOW_CACHE;
    return this.http.get<PaginatedResponse<IInsurerRepresentative>>(
      `${baseUrl()}/insurers/${id}/representatives`,
      {
        params: createQueryParams(param),
        headers: {
          [HttpCatchingHeaders]: '1',
        },
      },
    );
  }

  saveToLocalStorage(data: IInsurer) {
    localStorage.setItem('insurer', JSON.stringify(data));
  }

  getFromLocalStorage(): IInsurer {
    return JSON.parse(`${localStorage.getItem('insurer')}`) ?? null;
  }

  // Settings are no longer part of the insurer response: that endpoint is anonymous, so it
  // deliberately leaves them out. They have their own authorized endpoint.
  getSettings(id: number): Observable<InsurerSettings | null> {
    return this.http.get<InsurerSettings | null>(
      `${baseUrl()}/insurers/${id}/settings`,
    );
  }

  updateSettings(
    id: number,
    payload: Partial<InsurerSettings>,
  ): Observable<InsurerSettings> {
    return this.http.put<InsurerSettings>(
      `${baseUrl()}/insurers/${id}/settings`,
      { ...payload, insurerId: id },
    );
  }

  addUser(
    insurerId: number,
    user: Partial<IRepresentativeRequest>,
  ): Observable<IRepresentative> {
    return this.http.post<IRepresentative>(
      `${baseUrl()}/insurers/${insurerId}/representatives`,
      user,
    );
  }
}
